import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { WidgetConfigProvider } from "@/context/WidgetConfigContext";
import {
  ChatContainer,
  ChatHeaderSlot,
  ChatMessagesSlot,
  ChatInputSlot,
} from "@/components/chat/ChatContainer";
import { ChatHeader } from "@/components/chat/ChatHeader";
import { ChatMessages } from "@/components/chat/ChatMessages";
import { ChatInput } from "@/components/chat/ChatInput";
import { ChatError } from "@/components/chat/ChatError";
import { useChat } from "@/hooks/useChat";
import { useVoice } from "@/hooks/useVoice";
import { getErrorType } from "@/lib/errorUtils";
import { MessageType, type WidgetConfig, type ChatMessage } from "shared";

// Voice components
import {
  VoiceContainer,
  VoiceContentSlot,
} from "@/components/voice/VoiceContainer";
import { VoiceIdleScreen } from "@/components/voice/VoiceIdleScreen";
import { VoiceActiveScreen } from "@/components/voice/VoiceActiveScreen";
import { WidgetTabs, type TabType } from "@/components/widget/WidgetTabs";

// Server URL
const SERVER_URL = import.meta.env.VITE_SERVER_URL || "http://localhost:3002";

/**
 * Main chat page component
 * Fetches widget config and renders chat interface
 */
export default function ChatPage() {
  const { assistantId } = useParams<{ assistantId: string }>();
  const [widgetConfig, setWidgetConfig] = useState<WidgetConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);

  // Fetch widget configuration
  useEffect(() => {
    if (!assistantId) {
      setConfigError("No assistant ID provided");
      setConfigLoading(false);
      return;
    }

    async function fetchConfig() {
      try {
        const response = await fetch(
          `${SERVER_URL}/api/public/widget/${assistantId}`,
        );

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("Assistant not found");
          }
          throw new Error("Failed to load chat configuration");
        }

        const config: WidgetConfig = await response.json();
        setWidgetConfig(config);
      } catch (err) {
        console.error("Config fetch error:", err);
        setConfigError(
          err instanceof Error ? err.message : "Failed to load chat",
        );
      } finally {
        setConfigLoading(false);
      }
    }

    fetchConfig();
  }, [assistantId]);

  // Loading state
  if (configLoading) {
    return (
      <div className="flex items-center justify-center h-screen-safe bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (configError || !widgetConfig) {
    return (
      <div className="h-screen-safe bg-background">
        <ChatError
          type={getErrorType(configError || "unknown")}
          message={configError || undefined}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <WidgetConfigProvider config={widgetConfig}>
      <TabbedWidget assistantId={assistantId!} config={widgetConfig} />
    </WidgetConfigProvider>
  );
}

interface TabbedWidgetProps {
  assistantId: string;
  config: WidgetConfig;
}

/**
 * Tabbed widget with Chat and Voice support
 * Chat state is lifted here so the shared header can access resetSession/loadHistory
 */
function TabbedWidget({ assistantId, config }: TabbedWidgetProps) {
  const [activeTab, setActiveTab] = useState<TabType>(
    config.default_tab || "chat",
  );

  // Lift useChat to this level so the header can access resetSession and loadHistory
  const {
    messages,
    isConnecting,
    isConnected,
    isRefreshing,
    streamingMessageId,
    error,
    sendMessage,
    loadHistory,
    resetSession,
  } = useChat({
    assistantId,
  });

  // Check if voice is enabled
  const voiceEnabled = config.voice_enabled;

  return (
    <div className="flex flex-col h-full bg-background overflow-hidden">
      {/* Header - wired to real chat functions */}
      <ChatHeader
        onRefresh={loadHistory}
        onNewSession={resetSession}
        isRefreshing={isRefreshing}
        onClose={() => {
          window.parent.postMessage({ type: "SMALLFORCE_CLOSE" }, "*");
        }}
      />

      {/* Tabs (only if voice is enabled) */}
      {voiceEnabled && (
        <WidgetTabs activeTab={activeTab} onTabChange={setActiveTab} />
      )}

      {/* Content area with smooth transition */}
      <div className="flex-1 overflow-hidden relative">
        {/* Chat Tab */}
        <div
          className={`absolute inset-0 transition-all duration-300 ease-in-out ${
            activeTab === "chat"
              ? "opacity-100 translate-x-0"
              : "opacity-0 -translate-x-full pointer-events-none"
          }`}
        >
          <ChatContent
            assistantId={assistantId}
            config={config}
            messages={messages}
            isConnecting={isConnecting}
            isConnected={isConnected}
            streamingMessageId={streamingMessageId}
            error={error}
            sendMessage={sendMessage}
          />
        </div>

        {/* Voice Tab */}
        {voiceEnabled && (
          <div
            className={`absolute inset-0 transition-all duration-300 ease-in-out ${
              activeTab === "voice"
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-full pointer-events-none"
            }`}
          >
            <VoiceContent assistantId={assistantId} />
          </div>
        )}
      </div>
    </div>
  );
}

interface ChatContentProps {
  assistantId: string;
  config: WidgetConfig;
  messages: ChatMessage[];
  isConnecting: boolean;
  isConnected: boolean;
  streamingMessageId: string | null;
  error: string | null;
  sendMessage: (content: string) => void;
}

/**
 * Chat content component - receives chat state from parent
 */
function ChatContent({
  assistantId,
  config,
  messages,
  isConnecting,
  isConnected,
  streamingMessageId,
  error,
  sendMessage,
}: ChatContentProps) {
  // Get first message (use config value or default)
  const firstMessage = config.first_message || "Hi! How can I help you today?";

  // Add first message if no messages yet and connected
  const displayMessages: ChatMessage[] =
    messages.length === 0 && isConnected
      ? [
          {
            id: "first-message",
            type: MessageType.ASSISTANT_MESSAGE,
            session_id: "",
            content: firstMessage,
            sender_id: assistantId,
            created_at: new Date().toISOString(),
          } as ChatMessage,
        ]
      : messages;

  // If there's an error, show error component
  if (error) {
    return (
      <ChatError
        type={getErrorType(error)}
        message={error}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <ChatContainer>
      <ChatHeaderSlot>
        {/* Empty - header is shared at TabbedWidget level */}
        <div className="hidden" />
      </ChatHeaderSlot>

      <ChatMessagesSlot>
        <ChatMessages
          messages={displayMessages}
          isLoading={isConnecting}
          streamingMessageId={streamingMessageId}
        />
      </ChatMessagesSlot>

      <ChatInputSlot>
        <ChatInput
          onSend={sendMessage}
          disabled={!isConnected}
          placeholder={isConnecting ? "Connecting..." : "Type a message..."}
        />
      </ChatInputSlot>
    </ChatContainer>
  );
}

interface VoiceContentProps {
  /** Internal SmallForce assistant ID */
  assistantId: string;
}

/**
 * Voice content component
 * Uses secure token-based authentication - credentials are fetched on-demand
 */
function VoiceContent({ assistantId }: VoiceContentProps) {
  const {
    callStatus,
    isMuted,
    volumeLevel,
    error,
    startCall,
    endCall,
    toggleMute,
  } = useVoice({
    assistantId,
  });

  // Determine which screen to show
  const showActiveScreen = callStatus === "active";
  const isConnecting = callStatus === "connecting";

  return (
    <VoiceContainer className="h-full">
      <VoiceContentSlot>
        {error ? (
          <div className="text-center p-8">
            <p className="text-destructive text-sm mb-4">{error}</p>
            <button
              onClick={startCall}
              className="text-primary underline text-sm"
            >
              Try again
            </button>
          </div>
        ) : showActiveScreen ? (
          <VoiceActiveScreen
            isMuted={isMuted}
            volumeLevel={volumeLevel}
            onToggleMute={toggleMute}
            onEndCall={endCall}
          />
        ) : (
          <VoiceIdleScreen
            onStartCall={startCall}
            isConnecting={isConnecting}
          />
        )}
      </VoiceContentSlot>
    </VoiceContainer>
  );
}
